import cx from 'classnames';

import uploadSvg from 'assets/icons/upload.svg';
import { ACCEPTED_FILE_TYPE_XLS } from 'consts/fileType';
import Auth from 'modules/Auth';
import { FILE_SIZE_10MB, FILE_SIZE_50MB } from 'modules/api/constants';
import { PermissionsEnum } from 'consts/permissions';

import withLoader from 'components/hocs/withLoader';
import FileUpload from 'lib/FileUpload';
import Table from 'lib/Table/Table';
import { TableHeight, TableRowHeight } from 'lib/Table/Table.types';
import Button, { Color as ButtonColor, Size } from 'lib/Button';
import Card from 'components/common/Card';
import { CardTheme } from 'components/common/Card/Card.types';
import Modal from 'lib/Modal';
import PageSize from 'lib/PageSize';
import FileDetails from './FileDetails';
import { useSetupContent } from './useSetupContent';
import { useSetupTable } from './useSetupTable';
import { useAssetTemplateV2Enabled } from './useAssetTemplateV2Enabled';

const pageSizeKey = 'setup';

const SetupContent = (): React.ReactElement => {
  const { isAssetTemplateV2Enabled } = useAssetTemplateV2Enabled();
  const { getTableApi, onPageSizeChange, pageSize, refreshTable } = useSetupTable({
    pageSizeKey,
    isAssetTemplateV2Enabled,
  });

  const { fileName, onFileChange, columnDefs, onUploadFiles, setupDetails, isOpen, handleCloseModal } = useSetupContent(
    { resetTableApi: refreshTable, isAssetTemplateV2Enabled },
  );

  return (
    <>
      {Auth.hasPermission(PermissionsEnum.SETUP_UPLOAD) && (
        <Card cardTheme={CardTheme.WHITE}>
          <div className="flex gap-x-7">
            <div className="w-88">
              <FileUpload
                acceptFileType={ACCEPTED_FILE_TYPE_XLS}
                maxFileSize={isAssetTemplateV2Enabled ? FILE_SIZE_50MB : FILE_SIZE_10MB}
                placeholder={fileName || 'Upload asset data file'}
                onFileChange={onFileChange}
              />
            </div>
            <Button
              onClick={onUploadFiles}
              color={ButtonColor.PRIMARY}
              size={Size.LARGE}
              svg={uploadSvg}
              label="Upload file"
            />
          </div>
        </Card>
      )}
      <div className="flex justify-between mb-3">
        <PageSize
          dataTestId="page-size-selection"
          onPageSizeChange={onPageSizeChange}
          pageSize={pageSize}
          pageInfoText="setup files per page"
        />
        <Button onClick={refreshTable} color={ButtonColor.TERTIARY} size={Size.LARGE} label="Refresh asset data" />
      </div>
      <div className={cx('h-[calc(100vh-var(--header-height)*3.5)]')}>
        <Table
          dataTestId="setup-table"
          key={`${pageSize}-${isAssetTemplateV2Enabled}`}
          columnDefs={columnDefs}
          getTableApi={getTableApi}
          extraGridOptions={{
            rowModelType: 'infinite',
            rowHeight: TableRowHeight.NORMAL,
            pagination: true,
            paginationPageSize: pageSize,
            cacheBlockSize: pageSize,
            maxBlocksInCache: 1,
            enableCellTextSelection: true,
            suppressBrowserResizeObserver: true,
          }}
          tableHeight={TableHeight.FIT_SCREEN}
        />
      </div>
      <Modal isOpen={isOpen} onClose={handleCloseModal}>
        <FileDetails setupDetails={setupDetails} />
      </Modal>
    </>
  );
};

export default withLoader(SetupContent);
